import { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import fetchMissingToken from 'actions/fetchMissingToken.action';
import listSalons from 'actions/listSalons.action';
import postPin from 'actions/pin/postPin.action';
import PinForm from 'components/core/PinForm.component';
import selectIsLoggedIn from 'selectors/selectIsLoggedIn.selector';
import selectLocked from 'selectors/selectLocked';
import selectLockEnabled from 'selectors/selectLockEnabled';
import selectSupportedSalons from 'selectors/selectSupportedSalons';
import { selectLatestUiActionTime, selectLockToken } from 'selectors/simple';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { isEnvTest } from 'utils/env/determine.util';
import logger from 'utils/logger.util';
import checkSupportSalon from 'utils/salons/checkSupportSalon';

import { uiSetLock } from 'lib/actions';
import checkLock from 'lib/checkLock';

const L = logger('PinWatcher');

const TIMEOUT = Object.freeze({
  immediate: 0,
  quick: 1000,
  normal: 5000,
});

const PinWatcher = memo(() => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const hasToken = useSelector((state) => !!selectLockToken(state));
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const latest = useSelector(selectLatestUiActionTime);
  const locked = useSelector(selectLocked);
  const lockEnabled = useSelector(selectLockEnabled);
  const supportSalons = useSelector(selectSupportedSalons);
  const threshold = useSelector((state) => state?.lib?.threshold);

  const timeoutIdRef = useRef(null);
  const recheckIdRef = useRef(null);
  const mountedRef = useRef(true);

  const lock = useCallback(() => void dispatch(uiSetLock(true)), [dispatch]);

  const handleOnPin = useCallback((pin) => void dispatch(postPin({ pin, salonId: fromAnyToInteger(id) })), [dispatch, id]);

  // Use refs for values accessed inside refreshTimeout to avoid stale closures
  const latestRef = useRef(latest);
  latestRef.current = latest;
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;
  const lockEnabledRef = useRef(lockEnabled);
  lockEnabledRef.current = lockEnabled;
  const supportSalonsRef = useRef(supportSalons);
  supportSalonsRef.current = supportSalons;
  const hasTokenRef = useRef(hasToken);
  hasTokenRef.current = hasToken;
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const refreshTimeoutRef = useRef(null);

  const refreshTimeout = useCallback(() => {
    const salonId = fromAnyToInteger(id);
    const support = checkSupportSalon({ salons: supportSalonsRef.current, salonId });

    const scheduleRecheck = (timeout) => {
      recheckIdRef.current = setTimeout(() => {
        if (mountedRef.current) refreshTimeoutRef.current();
      }, timeout);
    };

    if (support === null) {
      // null is the initial value, no true/false means no info from BE yet, ask for fresh data and schedule a recheck
      if (isLoggedInRef.current) {
        // try to refresh access to salons only if logged in
        dispatch(listSalons());
      }

      return void scheduleRecheck(TIMEOUT.quick);
    }

    if (!hasTokenRef.current || lockEnabledRef.current === null) {
      dispatch(fetchMissingToken({ salonId, support }));
      return void scheduleRecheck(TIMEOUT.quick);
    }

    if (support === true) {
      // this check will bail out even if lockEnabled is true
      return void clearTimeout(timeoutIdRef.current);
    }

    const {
      auto,
      diff,
      locked: isLocked,
      future,
      error,
    } = checkLock({
      latest: latestRef.current,
      threshold: thresholdRef.current,
      now: Date.now(),
    });
    if (error) return void L.warn('refreshTimeout()', error);

    if (timeoutIdRef.current) {
      // remove any previous timeout, then decide if a new one is needed
      clearTimeout(timeoutIdRef.current);
    }

    if (lockEnabledRef.current !== true) {
      return void scheduleRecheck(
        // no true/false means no info from BE yet, in that case schedule immediate recheck
        lockEnabledRef.current === true || lockEnabledRef.current === false ? TIMEOUT.normal : TIMEOUT.immediate,
      );
    }

    // just a simple handler that when time's up it switches state to locked
    // if it gets cleared before fired, will most likely be replaced with a new one
    const handler = () => {
      L.info('refreshTimeout()', 'handler()', 'Lockout time has been reached, locking access...');
      lock();
    };

    if (isLocked || !hasTokenRef.current) return void (timeoutIdRef.current = setTimeout(handler, 0));

    if (!auto) return void 1;
    if (!future) return void scheduleRecheck(TIMEOUT.normal);

    // needed for the clear above
    return void (timeoutIdRef.current = setTimeout(handler, diff));
  }, [dispatch, id, lock]);

  refreshTimeoutRef.current = refreshTimeout;

  // Start the timeout cycle on mount or when refreshTimeout changes (i.e. salon id changes)
  useEffect(() => {
    mountedRef.current = true;
    refreshTimeout();
    return () => {
      mountedRef.current = false;
      clearTimeout(timeoutIdRef.current);
      clearTimeout(recheckIdRef.current);
    };
  }, [refreshTimeout]);

  // Track previous values for componentDidUpdate comparison
  const prevLatestRef = useRef(latest);
  const prevLockedRef = useRef(locked);
  const prevHasTokenRef = useRef(hasToken);

  useEffect(() => {
    const prevLatest = prevLatestRef.current;
    const prevLocked = prevLockedRef.current;
    const prevHasToken = prevHasTokenRef.current;

    if (latest !== prevLatest || locked !== prevLocked || hasToken !== prevHasToken) {
      refreshTimeout();
    }

    prevLatestRef.current = latest;
    prevLockedRef.current = locked;
    prevHasTokenRef.current = hasToken;
  }, [latest, locked, hasToken, refreshTimeout]);

  // FIXME: write better tests that don't depend on BE to make this one go away
  const notTestingEnv = !isEnvTest();

  L.debug('render()', 'Empty because:', { lockEnabled, locked, hasToken, notTestingEnv });

  if (locked !== true && (lockEnabled !== true || hasToken)) return null;

  return <PinForm onPin={handleOnPin} />;
});

export default PinWatcher;
